import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { SpinnerIcon } from "~/components/icon";
import { useCustomerSearch } from "~/hooks/useCustomerSearch";
import { useKeybindings } from "~/hooks/useKeyBindings";
import type { CustomerSearchResult } from "~/types";
import { stripTagPrefix } from "~/utils";

import { CustomerResultsList } from "./CustomerResultsList";

interface CustomerSearchModalProps {
  onSuccess?: () => void;
}

export function CustomerSearchModal({ onSuccess }: CustomerSearchModalProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const normalizedQuery = useMemo(
    () => stripTagPrefix(searchQuery),
    [searchQuery],
  );

  const {
    results,
    loading,
    highlightedIndex: selectedIndex,
    setHighlightedIndex: setSelectedIndex,
  } = useCustomerSearch(
    normalizedQuery,
    normalizedQuery.length === 0 || normalizedQuery.length >= 2,
  );

  // Focus input on mount
  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  }, []);

  const handleSelect = useCallback(
    (customer: CustomerSearchResult) => {
      onSuccess?.();
      navigate(`/customers/${customer.id}`);
    },
    [onSuccess, navigate],
  );

  const emptyStateMessage = useMemo(() => {
    if (normalizedQuery.length === 0) return "Type to search...";
    if (normalizedQuery.length < 2) return "Type at least 2 characters...";
    return "No customers found";
  }, [normalizedQuery]);

  // Navigation keybindings
  useKeybindings(
    {
      ArrowDown: (e: KeyboardEvent) => {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
      },
      ArrowUp: (e: KeyboardEvent) => {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      },
      Enter: (e: KeyboardEvent) => {
        e.preventDefault();
        if (results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
      },
    },
    {
      allowedElements: ["INPUT", "BUTTON", "DIV", "BODY"],
      disallowedElements: [],
      enabled: true,
    },
  );

  return (
    <div className="space-y-3">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by customer name or ID..."
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          autoComplete="off"
        />
        {loading ? (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <SpinnerIcon className="h-4 w-4 animate-spin text-gray-400" />
          </div>
        ) : null}
      </div>

      <div className="h-64 overflow-y-auto rounded-md border border-gray-200">
        {loading ? (
          [...Array(6)].map((_, i) => (
            <div
              key={i}
              className="flex w-full animate-pulse items-center justify-between px-3 py-2"
            >
              <div className="flex flex-col gap-1.5">
                <div className="h-4 w-32 rounded bg-gray-200" />
                <div className="h-3 w-48 rounded bg-gray-100" />
              </div>
            </div>
          ))
        ) : results.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            {emptyStateMessage}
          </div>
        ) : (
          <CustomerResultsList
            results={results}
            highlightedIndex={selectedIndex}
            onHighlight={setSelectedIndex}
            onSelect={handleSelect}
          />
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <kbd className="rounded bg-gray-100 px-1.5 py-0.5">Up/Down</kbd>
          <span>navigate</span>
        </div>
        <div className="flex items-center gap-2">
          <kbd className="rounded bg-gray-100 px-1.5 py-0.5">Enter</kbd>
          <span>select</span>
        </div>
        <div className="flex items-center gap-2">
          <kbd className="rounded bg-gray-100 px-1.5 py-0.5">Esc</kbd>
          <span>close</span>
        </div>
      </div>
    </div>
  );
}
